/* eslint-disable require-await */
import axios from "axios";
import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";

const CORRELATION_STORAGE_KEY = "portal_correlation_id";

const createCorrelationId = (): string => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const getOrCreateCorrelationId = (): string => {
    const current = window.sessionStorage.getItem(CORRELATION_STORAGE_KEY);

    if (current) {
        return current;
    }

    const generated = createCorrelationId();
    window.sessionStorage.setItem(CORRELATION_STORAGE_KEY, generated);

    return generated;
};

const setDatadogCorrelationContext = (correlationId: string, jobId?: string): void => {
    datadogRum.setGlobalContextProperty("correlation_id", correlationId);
    datadogLogs.setGlobalContextProperty("correlation_id", correlationId);

    if (jobId) {
        datadogRum.setGlobalContextProperty("job_id", jobId);
        datadogLogs.setGlobalContextProperty("job_id", jobId);
        datadogRum.addAction("external_job_received", { job_id: jobId, correlation_id: correlationId });
    }
};

const apiClient = axios.create({
    baseURL: "",
    headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
    }
});

apiClient.interceptors.request.use(config => {
    const correlationId = getOrCreateCorrelationId();

    config.headers["X-Correlation-ID"] = correlationId;
    config.headers["X-Request-ID"] = correlationId;

    setDatadogCorrelationContext(correlationId);

    return config;
});

apiClient.interceptors.response.use(
    response => {
        const responseCorrelationId =
            response.data?.correlation_id ||
            response.data?.data?.correlation_id ||
            response.headers?.["x-correlation-id"] ||
            response.headers?.["x-request-id"];
        const responseJobId = response.data?.job_id || response.data?.data?.job_id;

        if (responseCorrelationId) {
            window.sessionStorage.setItem(CORRELATION_STORAGE_KEY, responseCorrelationId);
            setDatadogCorrelationContext(responseCorrelationId, responseJobId);
        } else if (responseJobId) {
            setDatadogCorrelationContext(getOrCreateCorrelationId(), responseJobId);
        }

        return response;
    },
    async error => {
        if (
            error.response?.status === 401 ||
            error.response?.data?.message === "Unauthenticated."
        ) {
            console.warn("Token expired. Redirecting to login...");

            window.location.href = route("login");
        }

        return Promise.reject(error);
    }
);

export { apiClient };
